import type { BillingBlockPayload } from '../types';

export interface BillingBlockCopy {
  title: string;
  body: string;
  primaryLabel?: string;
  secondaryLabel: string;
}

function money(amountMinor: number | undefined, currency = 'usd'): string {
  if (amountMinor === undefined) return '';
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(amountMinor / 100);
}

export function getBillingBlockCopy(block: BillingBlockPayload): BillingBlockCopy {
  const currency = block.currency || 'usd';
  switch (block.type) {
    case 'missing_subscription':
      return {
        title: 'Subscription required',
        body: block.requiredCapability
          ? `This action requires the ${block.requiredCapability} capability on this project.`
          : 'This action requires an active project subscription.',
        primaryLabel: block.canSelfServe ? 'View billing' : 'Ask project owner',
        secondaryLabel: 'Close',
      };
    case 'missing_ai_tokens':
      return {
        title: 'AI access required',
        body: 'This workflow can run only when the project has AI Tokens access or enough Segmently credits for AI usage.',
        primaryLabel: block.canSelfServe ? 'View billing' : 'Ask project owner',
        secondaryLabel: 'Close',
      };
    case 'quota_exhausted':
      return {
        title: 'AI token quota exhausted',
        body: 'The included AI token quota for this project has been used. Add an AI Tokens subscription or credits to continue.',
        primaryLabel: block.canSelfServe ? 'Buy credits' : 'Ask project owner',
        secondaryLabel: 'Close',
      };
    case 'insufficient_credits': {
      const shortfall = money(block.shortfallMinor, currency);
      const available = money(block.availableCreditsMinor, currency);
      return {
        title: 'Not enough credits',
        body: shortfall
          ? `This action needs about ${shortfall} more credits. Current available balance: ${available}.`
          : `This action needs more credits. Current available balance: ${available}.`,
        primaryLabel: block.canSelfServe ? 'Buy credits' : 'Ask project owner',
        secondaryLabel: 'Close',
      };
    }
    case 'missing_model_price':
      return {
        title: 'AI model is not priced',
        body: 'This AI model does not have a billing price configured yet. Ask an admin to update the AI token meter.',
        secondaryLabel: 'Close',
      };
    case 'billing_config_error':
    default:
      return {
        title: 'Billing is not configured',
        body: block.reason || 'This billing action cannot be completed until the billing catalog is configured.',
        secondaryLabel: 'Close',
      };
  }
}
